/**
 * Integrytas Group - Data Privacy & Compliance (DPC)
 * Page: DPC.REQUESTS
 *
 * Subject Access Request desk: raise DSARs and fulfil them. ACCESS/EXPORT gather
 * the subject's data across the subject-data map; ERASURE anonymizes it. Backed
 * by dpc_fulfill_request (admin-gated).
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert } from '../../lib/routed_writes';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Search } from 'lucide-react';
import type { AuthSession } from '../../types/auth';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'DPC.REQUESTS';

// Request workflow statuses (CHECK enum) → localized label + badge variant (§17.B11).
const STATUS_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  OPEN:        { labelKey: 'dpc_requests.status.OPEN',        label: 'Open',        variant: 'success' },
  IN_PROGRESS: { labelKey: 'dpc_requests.status.IN_PROGRESS', label: 'In progress', variant: 'warning' },
  FULFILLED:   { labelKey: 'dpc_requests.status.FULFILLED',   label: 'Fulfilled',   variant: 'success' },
  REJECTED:    { labelKey: 'dpc_requests.status.REJECTED',    label: 'Rejected',    variant: 'danger'  },
};

type ReqType = 'ACCESS' | 'EXPORT' | 'ERASURE' | 'RECTIFY' | 'RESTRICT';
interface RequestRow {
  dpc_request_id: number; request_type: ReqType; subject_entity: string; subject_id: string;
  status: string; result: unknown; created_at: string;
}

const REQUEST_GRID_COLS: GridColumnDef[] = [
  { key: 'request_type',   header: 'Type',    defaultWidth: 120 },
  { key: 'subject',        header: 'Subject', defaultWidth: 240 },
  { key: 'status',         header: 'Status',  defaultWidth: 130 },
  { key: 'created_at',     header: 'Created', defaultWidth: 180 },
];

export interface DpcRequestsPageProps { session: AuthSession }

export function DpcRequestsPage({ session }: DpcRequestsPageProps) {
  return (
    <PersonalizationProvider pageCode="DPC.REQUESTS" session={session}>
      <DpcRequestsPageInner session={session} />
    </PersonalizationProvider>
  );
}

function DpcRequestsPageInner({ session }: DpcRequestsPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [rows, setRows] = useState<RequestRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  // §17.B13: related-children expand/contract. A DSAR request references a single
  // subject and loads no child collections, so the disclosure renders inert.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [
    // TODO: wire relations
  ];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets (logical columns of the list).
  const [facets, setFacets] = useState<{ type: string[]; status: string[] }>({ type: [], status: [] });
  // New-request form
  const [rtype, setRtype] = useState<ReqType>('ACCESS');
  const [sEntity, setSEntity] = useState('');
  const [sId, setSId] = useState('');

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('dpc_request')
        .select('dpc_request_id, request_type, subject_entity, subject_id, status, result, created_at')
        .order('created_at', { ascending: false }).limit(100);
      if (e) throw e;
      setRows((data ?? []) as RequestRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const create = async () => {
    if (!sEntity.trim() || !sId.trim()) { setError(t('dpc_requests.err.subject_required', 'Subject entity and id are required.')); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      const { error: e } = await routedInsert(session, 'dpc_request', { request_type: rtype, subject_entity: sEntity.trim(), subject_id: sId.trim(), status: 'OPEN' });
      if (e) throw e;
      setSEntity(''); setSId(''); await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const fulfil = async (id: number, decision: 'FULFILL' | 'REJECT') => {
    if (decision === 'FULFILL' && !confirm(t('dpc_requests.confirm.fulfil', 'Fulfil this request? ERASURE permanently anonymizes the subject’s data.'))) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const { data, error: e } = await apiDb.rpc('dpc_fulfill_request', { p_request_id: id, p_decision: decision });
      if (e) throw e;
      const res = data as { ok: boolean; error_code?: string; status?: string };
      if (!res?.ok) throw new Error(res?.error_code ?? 'DPC.FULFIL_FAILED');
      setNotice(t('dpc_requests.notice.request_status', 'Request {{id}}: {{status}}.', { id, status: res.status }));
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      type:   uniq(rows.map(r => r.request_type)).map(v => ({ value: v, label: v })),
      status: uniq(rows.map(r => r.status)).map(v => ({ value: v, label: v })),
    };
  }, [rows]);

  const facetsActive = facets.type.length + facets.status.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ type: [], status: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // OR within a facet, AND across facets.
    const matchesFacets = (r: RequestRow) =>
      (facets.type.length   === 0 || facets.type.includes(r.request_type)) &&
      (facets.status.length === 0 || facets.status.includes(r.status));
    const base = (!q ? rows : rows.filter(r =>
      r.request_type.toLowerCase().includes(q) ||
      r.subject_entity.toLowerCase().includes(q) ||
      r.subject_id.toLowerCase().includes(q) ||
      r.status.toLowerCase().includes(q)));
    return base.filter(matchesFacets);
  }, [rows, filter, facets]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('dpc_requests.title', 'Subject Access Requests')}
          description={t('dpc_requests.list.description', 'Raise and fulfil DSARs — gather, export or erase a subject’s data across the subject-data map.')}
          actions={
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> {t('dpc_requests.refresh', 'Refresh')}
            </button>
          }
        />

        {error && (
          <ErrorBanner
            message={t('dpc_requests.error_banner', "Couldn't complete the last subject-access-request operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <div className="flex flex-wrap items-end gap-2 rounded border border-csm-border bg-white p-2">
          <select value={rtype} onChange={e => setRtype(e.target.value as ReqType)} className="h-8 rounded border border-csm-border px-2 text-csm-sm">
            {['ACCESS', 'EXPORT', 'ERASURE', 'RECTIFY', 'RESTRICT'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input value={sEntity} onChange={e => setSEntity(e.target.value)} placeholder={t('dpc_requests.placeholder.subject_entity', 'subject entity (e.g. eds_recipient)')} className="h-8 w-56 rounded border border-csm-border px-2 font-mono text-csm-sm" />
          <input value={sId} onChange={e => setSId(e.target.value)} placeholder={t('dpc_requests.placeholder.subject_id', 'subject id')} className="h-8 w-40 rounded border border-csm-border px-2 font-mono text-csm-sm" />
          {canAdd && (<button onClick={() => void create()} className="h-8 rounded border border-sidebar bg-sidebar px-3 text-csm-sm text-white hover:bg-sidebar-alt">{t('dpc_requests.raise_request', 'Raise Request')}</button>)}
        </div>

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('dpc_requests.filter_placeholder', 'Filter requests…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('dpc_requests.col.type', 'Type')}     options={facetOptions.type}   selected={facets.type}   onChange={v => setFacets(f => ({ ...f, type: v }))} />
            <FacetFilter label={t('dpc_requests.col.status', 'Status')} options={facetOptions.status} selected={facets.status} onChange={v => setFacets(f => ({ ...f, status: v }))} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('dpc_requests.working', 'Working…')}</span>}
              {notice && <span className="text-csm-xs text-primary">{notice}</span>}
              <PersonalizationToggle />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={REQUEST_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('dpc_requests.col.related', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('dpc_requests.col.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.dpc_request_id}>
                    <tr className="hover:bg-csm-surface-alt">
                      <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                        <RelatedExpandButton
                          open={detail.isOpen(r.dpc_request_id)}
                          hasChildren={hasRelated(r.dpc_request_id)}
                          onToggle={() => detail.toggle(r.dpc_request_id)}
                          expandLabel={t('dpc_requests.related.expand', 'Show related records')}
                          collapseLabel={t('dpc_requests.related.collapse', 'Hide related')}
                          tooltip={t('dpc_requests.related.tooltip', 'Show related records')}
                        />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 text-csm-sm"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'request_type' && <span className="font-mono font-semibold">{r.request_type}</span>}
                          {col.key === 'subject'      && <span className="font-mono text-csm-xs">{r.subject_entity}:{r.subject_id}</span>}
                          {col.key === 'status'       && (() => { const meta = STATUS_META[r.status]; return <StatusBadge variant={meta?.variant ?? 'neutral'} label={meta ? t(meta.labelKey, meta.label) : r.status} />; })()}
                          {col.key === 'created_at'   && <span className="text-csm-2xs text-csm-watermark">{new Date(r.created_at).toLocaleString()}</span>}
                        </td>
                      ))}
                      <td className="border-b border-csm-border-soft px-2 py-1 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {(r.status === 'OPEN' || r.status === 'IN_PROGRESS') && (
                            <>
                              {canChange && (<button onClick={() => void fulfil(r.dpc_request_id, 'FULFILL')} className="rounded border border-primary bg-primary px-2 py-0.5 text-csm-xs text-white hover:bg-primary-hover">{t('dpc_requests.action.fulfil', 'Fulfil')}</button>)}
                              {canChange && (<button onClick={() => void fulfil(r.dpc_request_id, 'REJECT')} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger">{t('dpc_requests.action.reject', 'Reject')}</button>)}
                            </>
                          )}
                          {r.result != null && <button onClick={() => setOpen(open === r.dpc_request_id ? null : r.dpc_request_id)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{open === r.dpc_request_id ? t('dpc_requests.action.hide', 'Hide') : t('dpc_requests.action.result', 'Result')}</button>}
                        </div>
                      </td>
                    </tr>
                    {detail.isOpen(r.dpc_request_id) && (
                      <tr>
                        <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                          <RelatedChildrenPanel groups={buildRelatedGroups(r.dpc_request_id)} session={session} />
                        </td>
                      </tr>
                    )}
                    {open === r.dpc_request_id && r.result != null && (
                      <tr>
                        <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft px-2 py-1">
                          <pre className="max-h-64 overflow-auto rounded bg-csm-surface-alt p-2 font-mono text-csm-2xs text-csm-ink-soft">{JSON.stringify(r.result, null, 2)}</pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="px-2 py-8 text-center">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('dpc_requests.empty', 'No requests.')} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>
    </div>
  );
}
